<template>
  <div class="setting-materials-edit-v2">
    <div class="materials-header">
      <h4>Configuración de Items</h4>
      <p class="section-info"><strong>Sección:</strong> {{ seccion.DescripcionSeccion }}</p>
    </div>

    <!-- Categorías de materiales seleccionadas -->
    <div
      v-if="categoriasMateriales.length > 0"
      class="categories-section"
    >
      <h5>Categorías de Items Seleccionadas</h5>
      <el-tag
        v-for="codigoCategoria in categoriasMateriales"
        :key="codigoCategoria"
        :closable="false"
        class="category-tag"
      >
        {{ obtenerNombreCategoria(codigoCategoria) }}
      </el-tag>
    </div>

    <div class="materials-content">
      <!-- Selector de materiales -->
      <div class="material-selector">
        <el-row :gutter="16">
          <el-col :span="18">
            <custom-select
              v-model="materialSeleccionado"
              label="Agregar Item"
              :field-key="'Codigo'"
              :field-label="'DescripcionInterna'"
              :settings="{
                placeholder: 'Seleccione un item para agregar',
                options: materialesDisponibles,
                clearable: true,
                filterable: true,
              }"
            />
          </el-col>
          <el-col
            :span="6"
            class="add-button-col"
          >
            <el-button
              type="primary"
              :icon="IconAdd"
              :disabled="!materialSeleccionado"
              style="margin-top: 32px"
              @click="agregarMaterial"
            >
              Agregar
            </el-button>
          </el-col>
        </el-row>
      </div>

      <!-- Lista de materiales agregados -->
      <div class="materials-list">
        <h5>Items Configurados</h5>

        <div
          v-if="materialesLocales.length === 0"
          class="no-materials"
        >
          <el-empty description="No hay items configurados">
            <el-button
              type="primary"
              @click="abrirSelectorMasivo"
            >
              Agregar items masivamente
            </el-button>
          </el-empty>
        </div>

        <el-table
          v-else
          :data="materialesLocales"
          border
          size="small"
          empty-text="No hay items configurados"
          max-height="400"
        >
          <!-- <el-table-column type="index" label="#" width="50" /> -->
          <el-table-column
            label="Código"
            width="100"
          >
            <template #default="scope">
              <span>{{ scope.row.Codigo || scope.row.CodigoMaterial }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="Categoría"
            min-width="150"
          >
            <template #default="scope">
              <span>{{ scope.row.MaterialTipo?.MaterialCategoria?.Descripcion }}</span>
            </template>
          </el-table-column>
          <el-table-column
            label="Descripción"
            min-width="200"
          >
            <template #default="scope">
              <span>{{ scope.row.DescripcionInterna }}</span>
            </template>
          </el-table-column>

          <el-table-column
            label="Cantidad"
            width="120"
          >
            <template #default="scope">
              <el-input-number
                ref="inputRef"
                v-model="scope.row.Cantidad"
                :min="0"
                :precision="2"
                :formatter="formatter"
                :parser="parser"
                size="small"
                style="width: 100%"
                @focus="handleFocus"
                @blur="handleBlur(scope.row.Cantidad)"
                @change="actualizarCantidad(scope.$index, $event)"
              />
            </template>
          </el-table-column>
          <!-- <el-table-column label="Unidad" width="100">
            <template #default="scope">
              <span>{{ scope.row.UnidadMedida || 'kg' }}</span>
            </template>
          </el-table-column> -->
          <el-table-column
            label="Acciones"
            width="80"
            align="center"
          >
            <template #default="scope">
              <el-button
                :icon="IconDelete"
                size="small"
                type="danger"
                title="Eliminar item"
                @click="eliminarMaterial(scope.$index)"
              />
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>

    <!-- Botones de acción -->
    <div class="action-buttons">
      <el-button @click="cancelar">Cancelar</el-button>
      <el-button
        type="primary"
        @click="guardar"
      >
        Guardar items
      </el-button>
    </div>

    <!-- Modal para selector masivo -->
    <el-dialog
      v-model="modalMasivoVisible"
      title="Agregar items Masivamente"
      width="60%"
      :close-on-click-modal="false"
    >
      <div class="massive-selector">
        <el-transfer
          v-model="materialesSeleccionadosMasivo"
          :data="materialesTransfer"
          :titles="['Items Disponibles', 'Items Seleccionados']"
          filterable
          :filter-method="filtrarMateriales"
          filter-placeholder="Buscar item..."
        />
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="cerrarSelectorMasivo">Cancelar</el-button>
          <el-button
            type="primary"
            @click="agregarMaterialesMasivo"
            >Agregar Seleccionados</el-button
          >
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
} from 'vue';
import {
  ElMessage,
  ElMessageBox,
} from 'element-plus';
import {
  Plus as IconAdd,
  Delete as IconDelete,
} from '@element-plus/icons-vue';

// Componentes
import CustomSelect from '@/shared/components/custom_select.vue';
import { useLocaleNumberInput } from '@/shared/composables/useLocaleNumberInput.js';

const { inputRef, formatter, parser, handleFocus, handleBlur } = useLocaleNumberInput(2);

// Stores (solo los necesarios)
// Services
import { MaterialService } from '@/services/material_service.js';
import { MaterialCategoriaService } from '@/services/material_categoria_service.js';

// Props
const props = defineProps({
  seccion: {
    type: Object,
    required: true,
  },
  categoriasMateriales: {
    type: Array,
    default: () => [],
  },
});

// Emits
const emit = defineEmits(['guardar', 'cancelar']);

// Estado reactivo
const materialSeleccionado = ref(null);
const materialesLocales = ref([]);
const modalMasivoVisible = ref(false);
const materialesSeleccionadosMasivo = ref([]);

// Datos de servicios
const materialesListData = ref([]);
const categoriasListData = ref([]);
const cargandoDatos = ref(false);

// Computed
const materialesDisponibles = computed(() => {
  // Filtrar materiales que ya están agregados
  const materialesYaAgregados = materialesLocales.value.map((m) => m.Codigo);

  // Si hay categorías seleccionadas, filtrar solo materiales de esas categorías
  // let materialesFiltrados = materialesListData.value

  // if (props.categoriasMateriales.length > 0) {
  //   materialesFiltrados = materialesFiltrados.filter(material =>
  //     props.categoriasMateriales.includes(material.CodigoMaterialCategoria)
  //   )
  // }

  return materialesListData.value.filter((m) => !materialesYaAgregados.includes(m.Codigo));
  // .map(material => ({
  //   value: material.Codigo,
  //   label: `${material.Codigo} - ${material.Descripcion}`,
  //   Codigo: material.Codigo,
  //   Descripcion: material.Descripcion,
  //   CodigoMaterialCategoria: material.CodigoMaterialCategoria,
  //   UnidadMedida: material.UnidadMedida
  // }))
});

const materialesTransfer = computed(() => {
  return materialesDisponibles.value.map((material) => ({
    key: material.Codigo,
    label: material.DescripcionInterna,
    disabled: false,
  }));
});

// const cantidadTotal = computed(() => {
//   return materialesLocales.value.reduce((total, material) => {
//     return total + (parseFloat(material.Cantidad) || 0)
//   }, 0)
// })

// Métodos
const obtenerNombreCategoria = (codigoCategoria) => {
  const categoria = categoriasListData.value.find((c) => c.Codigo === codigoCategoria);
  return categoria?.Descripcion || `Código: ${codigoCategoria}`;
};

// const obtenerCategoriaMaterial = (material) => {
//   const codigo = material.Codigo || material.CodigoMaterial
//   const materialInfo = materialesListData.value.find(m => m.Codigo === codigo)
//   return obtenerNombreCategoria(materialInfo?.CodigoMaterialCategoria)
// }

// Métodos para cargar datos
const cargarMateriales = async () => {
  try {
    // Si no hay categorías especificadas, no cargar nada
    if (!props.categoriasMateriales || props.categoriasMateriales.length === 0) {
      materialesListData.value = [];
      console.log('No hay categorías de items especificadas');
      return;
    }

    // Crear array de promesas para cada categoría
    const promesasMateriales = props.categoriasMateriales.map((codigoCategoria) =>
      MaterialService.findAll({
        page: 1,
        limit: 1000,
        Activo: true,
        categoria: codigoCategoria,
      }),
    );

    // Ejecutar todas las llamadas en paralelo
    const resultados = await Promise.all(promesasMateriales);

    // Combinar todos los materiales de todas las categorías
    const todosLosMateriales = [];
    resultados.forEach((result) => {
      if (result && result.data && Array.isArray(result.data)) {
        todosLosMateriales.push(...result.data);
      }
    });

    materialesListData.value = todosLosMateriales;
    console.log(
      `items cargados: ${todosLosMateriales.length} de ${props.categoriasMateriales.length} categorías`,
    );
  } catch (error) {
    console.error('Error al cargar items:', error);
    materialesListData.value = [];
  }
};

const cargarCategorias = async () => {
  try {
    const result = await MaterialCategoriaService.findAll();
    if (result && result.data && Array.isArray(result.data)) {
      categoriasListData.value = result.data.filter((c) => c.Activo);
    } else {
      categoriasListData.value = [];
    }
  } catch (error) {
    console.error('Error al cargar categorías:', error);
    categoriasListData.value = [];
  }
};

const agregarMaterial = () => {
  if (!materialSeleccionado.value) {
    console.log('No hay item seleccionado');
    return;
  }

  // El CustomSelect puede devolver un objeto o un string
  let materialInfo;
  if (typeof materialSeleccionado.value === 'object') {
    // Si es un objeto, ya tenemos toda la información
    materialInfo = materialSeleccionado.value;
  } else {
    // Si es un string, buscar en materialesDisponibles
    materialInfo = materialesDisponibles.value.find((m) => m.Codigo === materialSeleccionado.value);
  }

  if (!materialInfo) {
    console.log('No se encontró materialInfo');
    return;
  }

  const nuevoMaterial = {
    Codigo: materialInfo.Codigo,
    CodigoMaterial: materialInfo.Codigo,
    Descripcion: materialInfo.Descripcion,
    CodigoMaterialCategoria: materialInfo.CodigoMaterialCategoria,
    UnidadMedida: materialInfo.UnidadMedida || 'kg',
    Cantidad: 1,
  };

  materialesLocales.value.push(materialSeleccionado.value);
  // materialSeleccionado.value = null

  ElMessage.success('Item agregado correctamente');
};

const eliminarMaterial = async (index) => {
  try {
    await ElMessageBox.confirm('¿Está seguro de eliminar este item?', 'Confirmar eliminación', {
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      type: 'warning',
    });

    materialesLocales.value.splice(index, 1);
    ElMessage.success('Item eliminado correctamente');
  } catch {
    // Usuario canceló
  }
};

const abrirSelectorMasivo = () => {
  materialesSeleccionadosMasivo.value = [];
  modalMasivoVisible.value = true;
};

const cerrarSelectorMasivo = () => {
  modalMasivoVisible.value = false;
  materialesSeleccionadosMasivo.value = [];
};

const agregarMaterialesMasivo = () => {
  materialesSeleccionadosMasivo.value.forEach((codigoMaterial) => {
    const materialInfo = materialesDisponibles.value.find((m) => m.Codigo === codigoMaterial);
    if (materialInfo) {
      const nuevoMaterial = {
        Codigo: materialInfo.Codigo,
        CodigoMaterial: materialInfo.Codigo,
        Descripcion: materialInfo.Descripcion,
        CodigoMaterialCategoria: materialInfo.CodigoMaterialCategoria,
        UnidadMedida: materialInfo.UnidadMedida || 'kg',
        Cantidad: 1,
      };
      materialesLocales.value.push(nuevoMaterial);
    }
  });

  cerrarSelectorMasivo();
  ElMessage.success(`${materialesSeleccionadosMasivo.value.length} items agregados correctamente`);
};

const filtrarMateriales = (query, item) => {
  return item.label.toLowerCase().includes(query.toLowerCase());
};

const guardar = () => {
  emit('guardar', materialesLocales.value);
};

const cancelar = () => {
  emit('cancelar');
};

const inicializarMateriales = () => {
  if (props.seccion.materiales && Array.isArray(props.seccion.materiales)) {
    materialesLocales.value = props.seccion.materiales.map((material) => ({
      ...material,
      Cantidad: material.Cantidad || 1,
    }));
  } else {
    materialesLocales.value = [];
  }
};

// Watchers
watch(
  () => props.seccion,
  () => {
    inicializarMateriales();
  },
  { immediate: true, deep: true },
);

// Watcher para recargar materiales cuando cambien las categorías
watch(
  () => props.categoriasMateriales,
  async () => {
    await cargarMateriales();
  },
  { deep: true },
);

// Lifecycle
onMounted(async () => {
  try {
    cargandoDatos.value = true;

    // Cargar categorías y materiales
    await Promise.all([cargarCategorias(), cargarMateriales()]);

    inicializarMateriales();
  } catch (error) {
    console.error('Error al cargar items:', error);
    ElMessage.error('Error al cargar los items disponibles');
  } finally {
    cargandoDatos.value = false;
  }
});
</script>

<style scoped>
.setting-materials-edit-v2 {
  padding: 0;
}

.materials-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.materials-header h4 {
  margin: 0 0 8px 0;
  color: #2c3e50;
}

.section-info {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

.categories-section {
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.categories-section h5 {
  margin: 0 0 12px 0;
  color: #2c3e50;
  font-size: 14px;
}

.category-tag {
  margin-right: 8px;
  margin-bottom: 8px;
}

.materials-content {
  margin-bottom: 24px;
}

.material-selector {
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.add-button-col {
  display: flex;
  align-items: flex-end;
}

.materials-list h5 {
  margin: 0 0 16px 0;
  color: #2c3e50;
}

.no-materials {
  text-align: center;
  padding: 32px;
}

.materials-summary {
  margin-top: 16px;
}

.materials-summary .el-card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.materials-summary p {
  margin: 8px 0;
  color: #606266;
}

.action-buttons {
  margin-top: 32px;
  text-align: right;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.action-buttons .el-button {
  margin-left: 8px;
}

.massive-selector {
  height: 400px;
}

.massive-selector :deep(.el-transfer) {
  height: 100%;
}

.massive-selector :deep(.el-transfer-panel) {
  height: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
